import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const blank = { name: "", email: "", phone: "", address: "", tin: "", notes: "" };

export const ContactsPage = ({ kind, title, subtitle, testid }) => {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get(`/${kind}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/${kind}/${editing}`, form);
      else await api.post(`/${kind}`, form);
      toast.success("Saved");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/${kind}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader title={title} subtitle={subtitle}
        action={canWrite && (
          <button data-testid={`add-${testid}-button`} onClick={() => { setForm(blank); setEditing(null); setOpen(true); }}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New {testid}
          </button>
        )} />

      <Panel title={`${rows.length} records`}>
        {rows.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testid}-table`}>
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Name</th><th className="px-5 py-2.5">TIN</th>
                  <th className="px-5 py-2.5">Email</th><th className="px-5 py-2.5">Phone</th>
                  <th className="px-5 py-2.5">Address</th><th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 font-medium text-slate-900">{r.name}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-600">{r.tin || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{r.email || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{r.phone || "—"}</td>
                    <td className="px-5 py-3 text-slate-500">{r.address || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-2">
                          <button data-testid={`edit-${testid}-button`} onClick={() => { setForm({ ...blank, ...r }); setEditing(r.id); setOpen(true); }}
                            className="text-slate-400 hover:text-emerald-600" aria-label="Edit"><Pencil size={15} /></button>
                          <button data-testid={`delete-${testid}-button`} onClick={() => remove(r.id)}
                            className="text-slate-400 hover:text-rose-600" aria-label="Delete"><Trash2 size={15} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message={`No ${kind} yet.`} />}
      </Panel>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-md" data-testid={`${testid}-dialog`}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">{editing ? `Edit ${testid}` : `New ${testid}`}</h3>
              <button data-testid={`close-${testid}-dialog`} onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
              {[["Name", "name", true], ["TIN", "tin", false], ["Email", "email", false],
                ["Phone", "phone", false], ["Address", "address", false], ["Notes", "notes", false]].map(([label, key, req]) => (
                <div key={key} className={key === "address" || key === "notes" ? "sm:col-span-2" : ""}>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
                  <input data-testid={`${testid}-${key}-input`} required={req} value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
              ))}
              <button data-testid={`save-${testid}-button`} className="sm:col-span-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                Save
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export const Customers = () => (
  <ContactsPage kind="customers" testid="customer" title="Customers"
    subtitle="Manage the businesses and individuals you invoice." />
);

export const Vendors = () => (
  <ContactsPage kind="vendors" testid="vendor" title="Vendors & Suppliers"
    subtitle="Track suppliers, their TINs and withholding tax obligations." />
);
